import React from 'react';
import { Alert, Col, Spin, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import type { SalesOutlookResult } from '@/services/salesDashboard.service';

const { Text } = Typography;

export const SalesOutlookRibbon: React.FC<{
  data?: SalesOutlookResult;
  loading?: boolean;
  error?: string | null;
  formatCurrency: (v: number) => string;
}> = ({ data, loading, error, formatCurrency }) => {
  const navigate = useNavigate();

  if (loading && !data) {
    return (
      <ControlTowerMetricStrip zone="metricsOrders" title="Outlook">
        <Col span={24}>
          <Spin />
        </Col>
      </ControlTowerMetricStrip>
    );
  }

  if (error) {
    return <Alert type="warning" showIcon message={error} style={{ marginBottom: 12 }} />;
  }

  if (!data) return null;

  return (
    <div id="outlook" style={{ marginBottom: 8 }}>
      <ControlTowerMetricStrip zone="metricsOrders" title="Outlook (forward look)">
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Weighted pipeline"
            count={data.open_opportunity_count}
            amount={data.weighted_pipeline_value}
            formatCurrency={formatCurrency}
            urgency="neutral"
            footerLink={{
              label: 'Owner Intelligence',
              onClick: () => navigate(data.owner_intelligence_path),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Open accepted quotes"
            count={data.open_accepted_quote_count}
            amount={data.open_accepted_quote_value}
            formatCurrency={formatCurrency}
            urgency={data.open_accepted_quote_count > 0 ? 'attention' : 'neutral'}
            footerLink={{
              label: 'Conversion queue',
              onClick: () =>
                document
                  .getElementById('quotation-conversion')
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Estimated bookings"
            count={data.trailing_win_rate_pct != null ? Math.round(data.trailing_win_rate_pct) : 0}
            amount={data.estimated_bookings ?? undefined}
            formatCurrency={formatCurrency}
            valueDisplay={
              data.estimated_bookings != null
                ? formatCurrency(data.estimated_bookings)
                : '—'
            }
            urgency="neutral"
            extra={
              <Text type="secondary" style={{ fontSize: 11 }}>
                {data.trailing_win_rate_pct != null
                  ? `${data.trailing_win_rate_pct}% win · estimate`
                  : 'Win rate unavailable'}
              </Text>
            }
            footerLink={{
              label: 'Analytics',
              onClick: () => navigate(data.analytics_overview_path),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="AR overdue"
            count={0}
            amount={data.ar_overdue_total}
            formatCurrency={formatCurrency}
            valueDisplay={formatCurrency(data.ar_overdue_total)}
            urgency={data.ar_overdue_total > 0 ? 'critical' : 'neutral'}
            trend={data.ar_overdue_trend}
            footerLink={{
              label: 'Cash queue',
              onClick: () =>
                document
                  .getElementById('cash-overdue')
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="SO confirmed (7d)"
            count={data.so_confirmed_trend.reduce((s, p) => s + Number(p.value ?? 0), 0)}
            trend={data.so_confirmed_trend}
            urgency="neutral"
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <ControlTowerMetricTile
            title="Delivery→invoice lag"
            count={data.avg_delivery_to_invoice_lag_days ?? 0}
            valueDisplay={
              data.avg_delivery_to_invoice_lag_days != null
                ? `${data.avg_delivery_to_invoice_lag_days}d`
                : '—'
            }
            urgency={
              data.avg_delivery_to_invoice_lag_days != null && data.avg_delivery_to_invoice_lag_days > 14
                ? 'attention'
                : 'neutral'
            }
            footerLink={{
              label: 'Fulfilment',
              onClick: () =>
                document
                  .getElementById('delivery-queues')
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
            }}
          />
        </Col>
      </ControlTowerMetricStrip>
    </div>
  );
};

export default SalesOutlookRibbon;
